import './mainmenu.css';
import { t, tText, onLang } from './i18n';
import { buildWheelPicker, GAME_LEVELS } from './levelPicker';
import { tierLabel } from './tier';
import type { DailyState } from './DailyPanel';
import type { MyProfile, AuthProvider, RoomInfo, GameFormat } from '../net/protocol';
import type { VariantId } from '../config';
import { COURSES, type CourseId } from '../tutorial/missions';

/**
 * Full-screen start menu shown on load (and reopenable).
 *
 * Four rows, front to back: the wordmark; your identity as one centred line
 * (name, tier, average, coins — no bars or pips, just the numbers), with
 * separate 로그인/가입/카카오 buttons for guests; a pair of "what's happening"
 * card (이번주의 매치, our lead hype, spelled out — variant + target — and its
 * own top-5 board, stacked in one combined box) that gives a reason to open
 * the app even with nobody to play; and the three actions that actually
 * start something (온라인 대전, practice, 당구 교실).
 *
 * Practice has no level of its own — tapping it asks solo-or-AI first, then
 * swaps in the level screen (a scroll-wheel 다마 picker). 온라인 대전 has no
 * picker either — it opens the rooms screen (the open-room table + a
 * "방 만들기" button), one click behind the front screen rather than on it.
 */
export interface MainMenuCallbacks {
  onPractice(level: number): void;
  onVsAI(level: number): void;
  onOnline(): void;
  onGeneralAuth(): void;
  onKakaoLogin(): void;
  onStore(): void;
  onFeedback(): void;
  onDaily(): void;
  onTutorial(course: CourseId): void;
  /** Join or spectate an open room from the main-menu room list. */
  onJoinRoom(code: string): void;
}

/** Per-course progress, as reported by `MainMenu.setCourseProgress`. */
export interface CourseProgress {
  id: CourseId;
  status: 'new' | 'partial' | 'complete';
  done: number;
  total: number;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

/** Ranks 1-3 on the weekly board get a medal instead of a bare number. */
const MEDALS: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };

/** One of the practice-choice cards: icon, label, and a line on what it is. */
function modeCard(icon: string, labelKey: string, onClick: () => void): HTMLButtonElement {
  const b = el('button', 'menu-card-btn');
  const ic = el('span', 'menu-card-btn__icon');
  ic.textContent = icon;
  const label = el('span', 'menu-card-btn__label');
  tText(label, labelKey);
  b.append(ic, label);
  b.onclick = onClick;
  return b;
}

/**
 * One card in the tutorial course picker: icon, name, a static one-line blurb
 * of what it covers, and a `progress` span the caller fills in separately
 * (status isn't known until `setCourseProgress` runs).
 */
function courseCard(icon: string, id: CourseId, onClick: () => void): { btn: HTMLButtonElement; progress: HTMLElement } {
  const b = el('button', 'menu-card-btn');
  const ic = el('span', 'menu-card-btn__icon');
  ic.textContent = icon;
  const label = el('span', 'menu-card-btn__label');
  tText(label, `course.${id}.title`);
  const hint = el('span', 'menu-card-btn__hint');
  tText(hint, `course.${id}.hint`);
  const progress = el('span', 'menu-card-btn__progress');
  b.append(ic, label, hint, progress);
  b.onclick = onClick;
  return { btn: b, progress };
}

/** One of the row-4 action buttons: icon over a label, all equal weight. */
function actionBtn(icon: string, labelKey: string, cls: string, onClick: () => void): HTMLButtonElement {
  const b = el('button', `ui-btn menu-action ${cls}`);
  const ic = el('span', 'menu-action__icon');
  ic.textContent = icon;
  const label = el('span', 'menu-action__label');
  tText(label, labelKey);
  b.append(ic, label);
  b.onclick = onClick;
  return b;
}

export class MainMenu {
  private readonly overlay = el('div', 'menu-overlay ui-scrim');
  private readonly modeScreen = el('div', 'menu-screen');
  private readonly practiceScreen = el('div', 'menu-screen');
  private readonly levelScreen = el('div', 'menu-screen');
  private readonly courseScreen = el('div', 'menu-screen');
  private readonly roomsScreen = el('div', 'menu-screen');
  private readonly courseCards: { id: CourseId; progress: HTMLElement }[] = [];
  private readonly courseProgress = new Map<CourseId, CourseProgress>();
  private readonly picker: { el: HTMLElement; set: (level: number) => void; value: () => number };
  private pendingMode: 'solo' | 'ai' = 'solo';
  private level: number;
  private readonly identity = el('div', 'menu-identity-simple');
  private readonly identityName = el('span', 'menu-identity__name');
  private readonly identityStats = el('span', 'menu-identity__stats');
  private readonly tierGauge = el('div', 'menu-tiergauge');
  private readonly tierGaugeFill = el('div', 'menu-tiergauge__fill');
  private readonly tierGaugeMark = el('div', 'menu-tiergauge__mark');
  private readonly tierGaugeLo = el('span', 'menu-tiergauge__lo');
  private readonly tierGaugeHi = el('span', 'menu-tiergauge__hi');
  private readonly tierGaugeStatus = el('div', 'menu-tiergauge__status');
  private readonly authRow = el('div', 'menu-authrow');
  private readonly generalAuthBtn = el('button', 'ui-btn ui-btn--ghost ui-btn--sm');
  private readonly kakaoBtn = el('button', 'ui-btn ui-btn--sm menu-kakaobtn');
  private readonly kakaoLabel = el('span');
  private readonly storeBtn = el('button', 'ui-btn ui-btn--ghost ui-btn--sm');
  private readonly feedbackBtn = el('button', 'ui-btn ui-btn--ghost ui-btn--sm');
  private readonly missionCard = el('button', 'menu-mission');
  private readonly missionLabel = el('span', 'menu-mission__label');
  private readonly missionGoal = el('span', 'menu-mission__goal');
  private readonly missionDesc = el('span', 'menu-mission__desc');
  private readonly rankBoard = el('div', 'menu-rankboard');
  private readonly tutorialHint = el('span', 'menu-action__sub');
  private readonly activityLine = el('div', 'menu-activityline');
  private readonly roomTableWrap = el('div', 'menu-roomtable-wrap');
  private readonly roomTableBody = el('tbody');
  private readonly roomSearch = el('input', 'ui-input menu-roomsearch') as HTMLInputElement;
  private readonly roomVariantFilter = el('select', 'ui-select menu-roomfilter') as HTMLSelectElement;
  private readonly roomFormatFilter = el('select', 'ui-select menu-roomfilter') as HTMLSelectElement;
  private readonly cb: MainMenuCallbacks;
  private readonly roomPager = el('div', 'menu-roompager');
  private rooms: RoomInfo[] = [];
  private roomQuery = '';
  private roomVariant: VariantId | 'all' = 'all';
  private roomFormat: GameFormat | 'all' = 'all';
  private roomPage = 0;
  private static readonly ROOM_PAGE_SIZE = 50;
  private activityCounts = { practicing: 0, tutorial: 0 };
  private profile: MyProfile | null = null;
  /** Live 코인 balance (App.syncCoins). Drives which staked rooms are joinable. */
  private coins = 0;
  private guest = true;
  private hasKakao = false;
  private dailyState: DailyState | null = null;
  /** Tutorial progress, kept as state so one onLang subscription can re-render it. */
  private tutorialProgress: { status: 'new' | 'partial' | 'complete'; done: number; total: number } = {
    status: 'new',
    done: 0,
    total: 0,
  };

  constructor(root: HTMLElement, cb: MainMenuCallbacks, initialLevel: number) {
    this.cb = cb;
    this.level = initialLevel;
    this.overlay.id = 'main-menu';

    // --- row 1: wordmark --------------------------------------------------
    const brand = el('div', 'menu-brand');
    const mark = el('img', 'menu-brand__mark');
    mark.src = import.meta.env.BASE_URL + 'qqr_logo.png';
    mark.alt = '';
    mark.draggable = false;
    const title = el('div', 'menu-brand__title');
    // Domain letters spelled out under each title character (space excluded),
    // so "qqrdg.com" reads left-to-right across the columns underneath
    // "꾀꼬리 당구장".
    const TITLE_DOMAIN_COLS: Array<[string, string]> = [
      ['꾀', 'q'],
      ['꼬', 'q'],
      ['리', 'r'],
      [' ', ''],
      ['당', 'd'],
      ['구', 'g'],
      ['장', '.com'],
    ];
    for (const [ch, sub] of TITLE_DOMAIN_COLS) {
      const col = el('div', 'menu-brand__titlecol');
      const charEl = el('span', 'menu-brand__titlechar');
      charEl.textContent = ch === ' ' ? ' ' : ch;
      const subEl = el('span', 'menu-brand__titledomain');
      subEl.textContent = sub;
      col.append(charEl, subEl);
      title.append(col);
    }
    const words = el('div');
    words.append(title);
    brand.append(mark, words);

    // --- row 2: identity, centred, with separate auth buttons for guests --
    // Kakao leads (one click, no password) — it's why the registered-account
    // count moved at all — so it sits first, left of 로그인/가입.
    this.kakaoBtn.onclick = () => cb.onKakaoLogin();
    const kakaoIcon = el('span', 'menu-kakaobtn__icon');
    kakaoIcon.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 3C6.477 3 2 6.463 2 10.74c0 2.72 1.82 5.113 4.56 6.49-.2.73-.726 2.655-.83 3.067-.13.51.187.503.394.366.163-.108 2.59-1.757 3.64-2.474.71.103 1.446.157 2.236.157 5.523 0 10-3.463 10-7.74C22 6.463 17.523 3 12 3z"/></svg>';
    tText(this.kakaoLabel, 'mainmenu.kakaoLogin');
    this.kakaoBtn.append(kakaoIcon, this.kakaoLabel);
    this.generalAuthBtn.onclick = () => cb.onGeneralAuth();
    tText(this.generalAuthBtn, 'mainmenu.generalLogin');
    this.authRow.append(this.kakaoBtn, this.generalAuthBtn);
    const tierGaugeTrack = el('div', 'menu-tiergauge__track');
    tierGaugeTrack.append(this.tierGaugeFill, this.tierGaugeMark);
    const tierGaugeLabels = el('div', 'menu-tiergauge__labels');
    tierGaugeLabels.append(this.tierGaugeLo, this.tierGaugeHi);
    this.tierGauge.append(tierGaugeTrack, tierGaugeLabels, this.tierGaugeStatus);
    this.tierGauge.style.display = 'none';
    this.identity.append(this.identityName, this.identityStats, this.tierGauge, this.authRow);
    this.identity.style.display = 'none';

    // --- row 3: today's mission (lead hype) + its own top-5 board, one box -
    const missionIcon = el('span', 'menu-mission__icon');
    missionIcon.textContent = '🔥';
    const missionWords = el('div', 'menu-mission__words');
    tText(this.missionLabel, 'mainmenu.daily');
    missionWords.append(this.missionLabel, this.missionGoal, this.missionDesc);
    this.missionCard.append(missionIcon, missionWords);
    this.missionCard.onclick = () => cb.onDaily();
    onLang(() => this.renderMission());

    const rankPanel = el('div', 'menu-rankpanel');
    rankPanel.onclick = () => cb.onDaily();
    const rankLabel = el('div', 'ui-label menu-rankpanel__label');
    tText(rankLabel, 'mainmenu.topRanks');
    rankPanel.append(rankLabel, this.rankBoard);

    const infoRow = el('div', 'menu-inforow');
    infoRow.append(this.missionCard, rankPanel);
    // ranking sits to the right of the mission inside the same box; the
    // mission's own goal text renders below its label in muted gray.

    // --- row 4: the three actions --------------------------------------------
    // 온라인 대전 opens the room list (below) rather than jumping straight to
    // create/browse — one click in, not zero, keeps the front screen short.
    const online = actionBtn('🏆', 'mainmenu.onlinePlay', 'menu-action--primary', () => this.showRooms());
    const practice = actionBtn('🎯', 'mainmenu.practiceMenu', '', () => this.showPractice());
    const tutorial = actionBtn('🎓', 'mainmenu.tutorial', '', () => this.showCourses());
    tutorial.append(this.tutorialHint);
    onLang(() => this.syncTutorialHint());

    const actions = el('div', 'menu-actions');
    actions.append(online, practice, tutorial);

    tText(this.storeBtn, 'mainmenu.store');
    this.storeBtn.onclick = () => cb.onStore();
    this.storeBtn.style.display = 'none'; // shown once the server confirms the feature gate

    tText(this.feedbackBtn, 'mainmenu.feedback');
    this.feedbackBtn.onclick = () => cb.onFeedback();

    const utils = el('div', 'menu-utils');
    utils.append(this.storeBtn, this.feedbackBtn);

    this.modeScreen.append(infoRow, actions, utils);

    // --- rooms screen: open rooms (table) + who's around, behind 온라인 대전 -
    const roomsLbl = el('div', 'ui-label menu-sectionlabel');
    tText(roomsLbl, 'mainmenu.openRooms');

    // filters: variant + format narrow the list, the search box matches
    // against the room title and any seated player's name
    this.roomSearch.type = 'search';
    onLang(() => (this.roomSearch.placeholder = t('lobby.searchPlaceholder')));
    this.roomSearch.oninput = () => {
      this.roomQuery = this.roomSearch.value.trim().toLowerCase();
      this.roomPage = 0;
      this.renderRooms();
    };
    const variantAll = el('option');
    tText(variantAll, 'lobby.allVariants');
    variantAll.value = 'all';
    const variantOpts = (['straight-rail', 'three-cushion'] as VariantId[]).map((v) => {
      const o = el('option');
      o.value = v;
      tText(o, `variant.${v}`);
      return o;
    });
    this.roomVariantFilter.append(variantAll, ...variantOpts);
    this.roomVariantFilter.onchange = () => {
      this.roomVariant = this.roomVariantFilter.value as VariantId | 'all';
      this.roomPage = 0;
      this.renderRooms();
    };
    const formatAll = el('option');
    tText(formatAll, 'lobby.allFormats');
    formatAll.value = 'all';
    const formatOpts = (['1v1', '2v2', '1v1v1', '1v1v1v1'] as GameFormat[]).map((f) => {
      const o = el('option');
      o.textContent = f;
      o.value = f;
      return o;
    });
    this.roomFormatFilter.append(formatAll, ...formatOpts);
    this.roomFormatFilter.onchange = () => {
      this.roomFormat = this.roomFormatFilter.value as GameFormat | 'all';
      this.roomPage = 0;
      this.renderRooms();
    };
    const filterBar = el('div', 'menu-roomfilters');
    filterBar.append(this.roomSearch, this.roomVariantFilter, this.roomFormatFilter);

    const roomTable = el('table', 'menu-roomtable');
    const thead = el('thead');
    const headRow = el('tr');
    const thGame = el('th');
    tText(thGame, 'lobby.variant');
    const thPlayers = el('th');
    tText(thPlayers, 'lobby.players');
    const thAction = el('th');
    headRow.append(thGame, thPlayers, thAction);
    thead.appendChild(headRow);
    roomTable.append(thead, this.roomTableBody);
    this.roomTableWrap.appendChild(roomTable);
    const createBtn = el('button', 'ui-btn ui-btn--go ui-btn--block');
    tText(createBtn, 'mainmenu.createRoom');
    createBtn.onclick = () => cb.onOnline(); // -> App.openCreateRoom (hides the menu itself)
    const roomsBack = el('button', 'ui-btn ui-btn--ghost ui-btn--block');
    tText(roomsBack, 'mainmenu.back');
    roomsBack.onclick = () => this.showModes();
    this.roomsScreen.append(roomsLbl, this.activityLine, filterBar, this.roomTableWrap, this.roomPager, createBtn, roomsBack);

    // --- practice choice screen: solo or AI --------------------------------
    const practiceTitle = el('div', 'ui-label menu-sectionlabel');
    tText(practiceTitle, 'mainmenu.practiceChoose');
    const practiceGrid = el('div', 'menu-grid');
    practiceGrid.append(
      modeCard('🎯', 'mainmenu.practice', () => this.showLevels('solo')),
      modeCard('🤖', 'mainmenu.vsai', () => this.showLevels('ai'))
    );
    const practiceBack = el('button', 'ui-btn ui-btn--ghost ui-btn--block');
    tText(practiceBack, 'mainmenu.back');
    practiceBack.onclick = () => this.showModes();
    this.practiceScreen.append(practiceTitle, practiceGrid, practiceBack);

    // --- level screen (scroll-wheel picker + confirm) --------------------
    const lvlTitle = el('div', 'ui-label menu-sectionlabel');
    tText(lvlTitle, 'mainmenu.selectLevel');
    this.picker = buildWheelPicker(GAME_LEVELS, (lv) => (this.level = lv));
    const confirm = el('button', 'ui-btn ui-btn--go ui-btn--lg ui-btn--block');
    tText(confirm, 'mainmenu.confirm');
    confirm.onclick = () => {
      const lv = this.picker.value();
      this.level = lv;
      if (this.pendingMode === 'ai') cb.onVsAI(lv);
      else cb.onPractice(lv);
    };
    const back = el('button', 'ui-btn ui-btn--ghost ui-btn--block');
    tText(back, 'mainmenu.back');
    back.onclick = () => this.showPractice();
    this.levelScreen.append(lvlTitle, this.picker.el, confirm, back);

    // --- course picker: the three tutorials, each startable on its own -----
    const courseTitle = el('div', 'ui-label menu-sectionlabel');
    tText(courseTitle, 'mainmenu.courseChoose');
    const courseGrid = el('div', 'menu-grid menu-grid--courses');
    for (const c of COURSES) {
      const { btn, progress } = courseCard(c.icon, c.id, () => {
        this.hide();
        cb.onTutorial(c.id);
      });
      this.courseCards.push({ id: c.id, progress });
      courseGrid.appendChild(btn);
    }
    const courseBack = el('button', 'ui-btn ui-btn--ghost ui-btn--block');
    tText(courseBack, 'mainmenu.back');
    courseBack.onclick = () => this.showModes();
    this.courseScreen.append(courseTitle, courseGrid, courseBack);
    onLang(() => this.syncCourseProgress());

    onLang(() => this.renderIdentity());
    onLang(() => this.renderRooms());
    onLang(() => this.renderActivity());

    const panel = el('div', 'menu-panel ui-panel interactive');
    panel.append(brand, this.identity, this.modeScreen, this.practiceScreen, this.levelScreen, this.courseScreen, this.roomsScreen);
    this.overlay.appendChild(panel);
    root.appendChild(this.overlay);

    this.showModes();
    this.hide();
  }

  /** Hide every screen but `keep` — the single choke point every showX goes through. */
  private showOnly(keep: HTMLElement): void {
    for (const s of [this.modeScreen, this.practiceScreen, this.levelScreen, this.courseScreen, this.roomsScreen]) {
      s.style.display = s === keep ? 'flex' : 'none';
    }
  }

  private showPractice(): void {
    this.showOnly(this.practiceScreen);
  }

  private showLevels(mode: 'solo' | 'ai'): void {
    this.pendingMode = mode;
    this.showOnly(this.levelScreen);
    // defer so the wheel has its laid-out height before we scroll a value in
    requestAnimationFrame(() => this.picker.set(this.level));
  }

  private showCourses(): void {
    this.showOnly(this.courseScreen);
  }

  /** 온라인 대전: the open-room list, one click behind the front screen. */
  private showRooms(): void {
    this.showOnly(this.roomsScreen);
  }

  /**
   * Show the player's identity (name, tier, average, coins) as one centred
   * line under the wordmark, with separate 로그인/가입/카카오 buttons when the
   * account is a guest.
   */
  setProfile(name: string | null, profile: MyProfile | null, isGuest = true): void {
    this.profile = profile;
    this.guest = isGuest;
    if (!name) {
      this.identity.style.display = 'none';
      return;
    }
    this.identityName.textContent = name;
    this.identity.style.display = '';
    this.renderIdentity();
  }

  /** Keep the 코인 balance current: it gates the room list's Join buttons, so a
   *  payout or a purchase has to re-render the rooms too, not just the header. */
  setCoins(coins: number): void {
    if (coins === this.coins) return;
    this.coins = coins;
    this.renderRooms();
  }

  /** Only offer Kakao once the server reports it's actually configured. */
  setProviders(providers: AuthProvider[]): void {
    this.hasKakao = providers.some((p) => p.id === 'kakao');
    this.renderIdentity();
  }

  private renderIdentity(): void {
    if (this.identity.style.display === 'none') return;
    const p = this.profile;
    const tier = p?.tier ?? null;
    const parts: string[] = [tier != null ? tierLabel(tier) : t('mainmenu.guest')];
    const avg = p?.progress;
    if (avg?.trusted) parts.push(t('prog.average') + ' ' + avg.avg.toFixed(2));
    if (p) parts.push(`🪙 ${p.coins ?? 0}`);
    this.identityStats.textContent = parts.join(' · ');
    this.authRow.style.display = this.guest ? '' : 'none';
    this.kakaoBtn.style.display = this.guest && this.hasKakao ? '' : 'none';
    this.renderTierGauge();
  }

  /**
   * The 승급/강등 gauge: a bar spanning the relegation→promotion average
   * thresholds around your current tier, with a marker at your rolling
   * average, plus a status line saying how many qualifying games are left
   * either way. Before an average is trusted (< TRUST_INNINGS), the bar
   * instead shows calibration progress — there's nothing else honest to draw.
   */
  private renderTierGauge(): void {
    const pr = this.profile?.progress ?? null;
    if (!pr) {
      this.tierGauge.style.display = 'none';
      return;
    }
    this.tierGauge.style.display = '';
    this.tierGaugeMark.classList.remove('menu-tiergauge__mark--warn', 'menu-tiergauge__mark--up');
    this.tierGaugeStatus.classList.remove('menu-tiergauge__status--warn', 'menu-tiergauge__status--up');

    if (!pr.trusted) {
      const pct = Math.max(0, Math.min(1, pr.innings / pr.trustInnings));
      this.tierGaugeFill.style.width = `${pct * 100}%`;
      this.tierGaugeMark.style.display = 'none';
      this.tierGaugeLo.textContent = '';
      this.tierGaugeHi.textContent = '';
      this.tierGaugeStatus.textContent = t('prog.measuring', { a: pr.innings, b: pr.trustInnings });
      return;
    }

    const lo = pr.bandPrev;
    const hi = pr.bandNext ?? pr.bandNow + Math.max(pr.bandNow - lo, 0.01);
    const pct = Math.max(0, Math.min(1, (pr.avg - lo) / Math.max(hi - lo, 0.01)));
    this.tierGaugeFill.style.width = `${pct * 100}%`;
    this.tierGaugeMark.style.display = '';
    this.tierGaugeMark.style.left = `${pct * 100}%`;
    this.tierGaugeLo.textContent = lo.toFixed(2);
    this.tierGaugeHi.textContent = pr.bandNext != null ? hi.toFixed(2) : '';

    if (pr.next == null) {
      this.tierGaugeStatus.textContent = t('prog.atTop');
    } else if (pr.demo > 0) {
      this.tierGaugeMark.classList.add('menu-tiergauge__mark--warn');
      this.tierGaugeStatus.classList.add('menu-tiergauge__status--warn');
      this.tierGaugeStatus.textContent = t('prog.demoWarn', { n: pr.demoNeed - pr.demo });
    } else if (pr.promo > 0) {
      this.tierGaugeMark.classList.add('menu-tiergauge__mark--up');
      this.tierGaugeStatus.classList.add('menu-tiergauge__status--up');
      const left = pr.promoNeed - pr.promo;
      this.tierGaugeStatus.textContent = left <= 1 ? t('prog.promoting') : t('prog.toPromote', { n: left });
    } else {
      this.tierGaugeStatus.textContent = t('prog.holding', { t: pr.tier });
    }
  }

  /**
   * Say where the player is in 당구 교실, so the card reads as "start", "carry on
   * from step 6" or "done — but come back whenever".
   */
  setTutorialProgress(status: 'new' | 'partial' | 'complete', doneCount: number, total: number): void {
    this.tutorialProgress = { status, done: doneCount, total };
    this.syncTutorialHint();
  }

  private syncTutorialHint(): void {
    const { status } = this.tutorialProgress;
    if (status === 'new') this.tutorialHint.textContent = t('mainmenu.tutorialHint');
    else this.tutorialHint.textContent = '';
  }

  /** Say where the player stands in each of the three courses, for the picker cards. */
  setCourseProgress(list: readonly CourseProgress[]): void {
    for (const p of list) this.courseProgress.set(p.id, p);
    this.syncCourseProgress();
  }

  private syncCourseProgress(): void {
    for (const card of this.courseCards) {
      const p = this.courseProgress.get(card.id);
      if (!p) continue;
      const complete = p.status === 'complete';
      card.progress.textContent = t(complete ? 'mainmenu.courseComplete' : 'mainmenu.courseIncomplete');
      card.progress.classList.toggle('menu-card-btn__progress--complete', complete);
      card.progress.classList.toggle('menu-card-btn__progress--incomplete', !complete);
    }
  }

  /** Show/hide the store button per the server's 'itemStore' feature gate. */
  setStoreEnabled(enabled: boolean): void {
    this.storeBtn.style.display = enabled ? '' : 'none';
  }

  /**
   * Spell out this week's 이번주의 매치 in row 3 — variant + target — and its
   * own top-5 board, so the card carries real hype, not just a label.
   */
  setDaily(state: DailyState | null): void {
    this.dailyState = state;
    this.renderMission();
  }

  private renderMission(): void {
    const s = this.dailyState;
    if (!s) {
      this.missionGoal.textContent = '';
      this.missionDesc.textContent = '';
      this.rankBoard.replaceChildren();
      return;
    }
    this.missionGoal.textContent = `${t(`variant.${s.variant}`)} · ${t('mainmenu.missionGoal', { t: s.target })}`;
    // Lead with the player's own status here — that's what someone glancing at
    // the menu wants to know, not the goal restated (already above, in missionGoal).
    if (s.mine?.strokes != null) this.missionDesc.textContent = t('daily.best', { n: s.mine.strokes });
    else if (s.mine && s.mine.attempts > 0) this.missionDesc.textContent = t('daily.noClear', { n: s.mine.caroms });
    else this.missionDesc.textContent = t('daily.untried');

    this.rankBoard.replaceChildren();
    const top5 = s.entries.slice(0, 5);
    if (top5.length === 0) {
      const empty = el('div', 'menu-rankempty');
      tText(empty, 'lobby.noRanks');
      this.rankBoard.appendChild(empty);
      return;
    }
    for (const e of top5) {
      const row = el('div', 'menu-rankrow');
      const rank = el('span', 'menu-rankrow__rank');
      rank.textContent = MEDALS[e.rank] ?? `${e.rank}`;
      const name = el('span', 'menu-rankrow__name');
      name.textContent = e.name;
      row.append(rank, name);
      if (e.coins > 0) {
        const coins = el('span', 'menu-rankrow__coins');
        coins.textContent = `+${e.coins}`;
        row.append(coins);
      }
      const score = el('span', 'menu-rankrow__score');
      score.textContent = e.strokes != null ? t('daily.strokes', { n: e.strokes }) : t('daily.dnf');
      row.append(score);
      this.rankBoard.appendChild(row);
    }
  }

  /** Live headcount of who's around right now but not in an open room: solo/vs-AI
   *  practice and 당구 교실 lessons have no joinable session, so a count is all
   *  there is to show for them. Hidden entirely when nobody's doing either. */
  setActivity(practicing: number, tutorial: number): void {
    this.activityCounts = { practicing, tutorial };
    this.renderActivity();
  }

  private renderActivity(): void {
    const { practicing, tutorial } = this.activityCounts;
    const parts: string[] = [];
    if (practicing > 0) parts.push(t('mainmenu.practicingCount', { n: String(practicing) }));
    if (tutorial > 0) parts.push(t('mainmenu.tutorialCount', { n: String(tutorial) }));
    this.activityLine.textContent = parts.join(' · ');
    this.activityLine.style.display = parts.length ? '' : 'none';
  }

  /** The open-room list (waiting + in-progress), pushed continuously over the
   *  presence socket so it stays live while the menu just sits there. */
  setRooms(rooms: RoomInfo[]): void {
    this.rooms = rooms;
    this.renderRooms();
  }

  private filteredRooms(): RoomInfo[] {
    return this.rooms.filter((r) => {
      if (this.roomVariant !== 'all' && r.variant !== this.roomVariant) return false;
      if (this.roomFormat !== 'all' && r.format !== this.roomFormat) return false;
      if (this.roomQuery) {
        const haystack = `${r.name} ${r.playerNames.join(' ')}`.toLowerCase();
        if (!haystack.includes(this.roomQuery)) return false;
      }
      return true;
    });
  }

  private renderRooms(): void {
    const filtered = this.filteredRooms();
    const pageSize = MainMenu.ROOM_PAGE_SIZE;
    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
    this.roomPage = Math.min(this.roomPage, pageCount - 1);
    const rows = filtered.slice(this.roomPage * pageSize, (this.roomPage + 1) * pageSize);

    this.roomTableBody.replaceChildren();
    this.roomTableWrap.classList.toggle('is-empty', rows.length === 0);
    this.renderPager(filtered.length, pageCount);
    if (rows.length === 0) {
      const row = el('tr');
      const cell = el('td', 'menu-roomempty');
      cell.colSpan = 3;
      tText(cell, this.rooms.length === 0 ? 'lobby.noRooms' : 'lobby.noRoomsMatch');
      row.appendChild(cell);
      this.roomTableBody.appendChild(row);
      return;
    }
    for (const r of rows) {
      const row = el('tr');
      const gameCell = el('td', 'menu-roomnm');
      const variantIcon = r.variant === 'three-cushion' ? '🎱' : '🔴';
      gameCell.textContent = `${variantIcon} ${t(`variant.${r.variant}`)} · ${r.format}`;
      const playersCell = el('td', 'menu-roomplayers');
      const who = el('div', 'menu-roomwho');
      who.textContent = r.playerNames.length ? r.playerNames.join(', ') : t('lobby.empty');
      const meta = el('div', 'menu-roommeta');
      const statusTxt = r.status === 'playing' ? t('lobby.playing') : t('lobby.waitingShort');
      const stake = r.stake || 0;
      meta.textContent = `${r.players}/${r.capacity} · ${statusTxt}` + (stake > 0 ? ` · ${t('lobby.stakeBadge', { n: String(stake) })}` : '');
      playersCell.append(who, meta);
      const actionCell = el('td', 'menu-roomaction');
      const joinBtn = el('button', 'menu-joinbtn');
      tText(joinBtn, r.status === 'playing' ? 'lobby.watch' : 'lobby.join');
      // Taking a seat in a staked room costs that stake, so a short balance
      // can't join — but watching a game in progress is free, and the server
      // only ever seats you in a *waiting* room.
      const tooRich = r.status === 'waiting' && stake > this.coins;
      if (tooRich) {
        joinBtn.disabled = true;
        // the tooltip sits on the cell, not the button: a disabled button takes
        // no pointer events, so its own title would never show on hover
        actionCell.title = t('lobby.joinStakeShort', { n: String(stake), have: String(this.coins) });
      }
      joinBtn.onclick = r.stub
        ? () => {
            // not a real, joinable session — say so right on the row instead
            // of round-tripping to the server (which would tell us the same)
            meta.textContent = t('lobby.roomGone');
            joinBtn.disabled = true;
          }
        : () => this.cb.onJoinRoom(r.code);
      actionCell.appendChild(joinBtn);
      row.append(gameCell, playersCell, actionCell);
      this.roomTableBody.appendChild(row);
    }
  }

  /** Prev/next + "showing X-Y of Z", hidden entirely when everything fits on one page. */
  private renderPager(total: number, pageCount: number): void {
    this.roomPager.replaceChildren();
    if (pageCount <= 1) return;
    const pageSize = MainMenu.ROOM_PAGE_SIZE;
    const from = this.roomPage * pageSize + 1;
    const to = Math.min(total, from + pageSize - 1);

    const prev = el('button', 'ui-btn ui-btn--ghost ui-btn--sm');
    tText(prev, 'lobby.prevPage');
    prev.disabled = this.roomPage === 0;
    prev.onclick = () => {
      this.roomPage--;
      this.renderRooms();
    };
    const info = el('span', 'menu-roompager__info');
    info.textContent = t('lobby.pageInfo', { from: String(from), to: String(to), total: String(total) });
    const next = el('button', 'ui-btn ui-btn--ghost ui-btn--sm');
    tText(next, 'lobby.nextPage');
    next.disabled = this.roomPage >= pageCount - 1;
    next.onclick = () => {
      this.roomPage++;
      this.renderRooms();
    };
    this.roomPager.append(prev, info, next);
  }

  showModes(): void {
    this.showOnly(this.modeScreen);
  }

  show(): void {
    this.showModes();
    this.overlay.classList.add('open');
  }

  hide(): void {
    this.overlay.classList.remove('open');
  }
}
